import React, { useState } from 'react';
import TrackPills from './TrackPills';
import TrackEditor, { EditTracksButton } from './TrackEditor';
import './TracksOnFile.css';

/**
 * "Tracks on file" (2026-09-24): the student's filed cuts that no song on the
 * board claims (studentTracksModel → unmatchedTrackDocs). Without this group a
 * cut filed before its song reached the board was unreachable on every staff
 * surface; Calvin Nelson's and Natalie Erdmann's were. One row per song, each
 * with the same TrackPills the song strip uses, wired to the page's single
 * player so two cuts can never play over each other.
 *
 * Renders nothing when every filed track already sits under a song. The note
 * under the heading says why these are here, and what makes one move: adding
 * the song to the board (the match is the song's title or its registry link).
 */
// `hideHeading` (Lesson Dashboard pass 3): inside the Tracks mode disclosure the
// disclosure's own label ("Tracks on file · N") is the heading.
// `edit` (2026-09-29, admins only): { docs, uid, onChanged } — each row gets
// an Edit control opening TrackEditor for that pointer doc. Renaming the song
// here to the board song's name is also how a stray track moves under it.
const TracksOnFile = ({ tracks = [], player, headingClassName = '', className = '', hideHeading = false, edit = null }) => {
	const [editing, setEditing] = useState(null);
	if (!Array.isArray(tracks) || tracks.length === 0 || !player) return null;
	return (
		<section className={`tracks-on-file ${className}`.trim()} aria-label="Tracks on file">
			{!hideHeading && <h3 className={headingClassName || 'tracks-on-file__heading'}>Tracks on file</h3>}
			<p className="tracks-on-file__note">
				Not in the repertoire yet. Add the song in a lesson and its tracks move under it.
			</p>
			<ul className="tracks-on-file__list">
				{tracks.map((t) => (
					<li key={t.id || t.songName} className="tracks-on-file__item">
						<div className="tracks-on-file__song">
							<i className="fa-sharp fa-solid fa-music" aria-hidden="true" />
							<span>{t.songName}</span>
							{t.show ? <span className="tracks-on-file__show">{t.show}</span> : null}
							{edit && (
								<span className="tracks-on-file__edit">
									<EditTracksButton
										label="Edit"
										open={editing === t.id}
										onClick={() => setEditing(editing === t.id ? null : t.id)}
									/>
								</span>
							)}
						</div>
						<TrackPills files={t.files} player={player} compact />
						{edit && editing === t.id && (
							<TrackEditor
								docs={(edit.docs || []).filter((d) => d.id === t.id)}
								uid={edit.uid}
								onChanged={edit.onChanged}
								onClose={() => setEditing(null)}
							/>
						)}
					</li>
				))}
			</ul>
		</section>
	);
};

export default TracksOnFile;
